/**
 * DateResponse — native HTML5 date picker. Value is ISO YYYY-MM-DD.
 *
 * Uses `<input type="date">` so mobile gets the OS-native date UI (iOS
 * wheel, Android calendar) and desktop gets the browser's popup.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { ResponseCoreProps } from './types';
import './responses.css';

interface Props extends ResponseCoreProps<string> {
  /** ISO YYYY-MM-DD. */
  min?: string;
  /** ISO YYYY-MM-DD. */
  max?: string;
}

export function DateResponse({
  label,
  hideLabel,
  initialValue = '',
  onChange,
  onSubmit,
  disabled,
  autoFocus,
  hideSubmit,
  submitLabel = 'Send',
  helper,
  min,
  max,
}: Props) {
  const [value, setValue] = useState<string>(initialValue ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  function update(next: string) {
    setValue(next);
    onChange?.(next);
  }

  function commit(e?: FormEvent) {
    if (e) e.preventDefault();
    if (!value) return;
    onSubmit?.(value);
  }

  const id = `cf-date-${label.replace(/\s+/g, '-')}`;
  const labelEl = hideLabel ? (
    <label htmlFor={id} className="cf-visually-hidden">
      {label}
    </label>
  ) : (
    <label htmlFor={id} className="cf-response__label">
      {label}
    </label>
  );

  return (
    <form className="cf-response cf-date-response" onSubmit={commit}>
      {labelEl}
      <input
        ref={inputRef}
        id={id}
        type="date"
        className="cf-input"
        value={value}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(e) => update(e.target.value)}
      />
      {helper && <div className="cf-response__helper">{helper}</div>}
      {!hideSubmit && (
        <div className="cf-response__actions">
          <button
            type="submit"
            className="cf-btn cf-btn--primary"
            disabled={disabled || !value}
          >
            {submitLabel}
          </button>
        </div>
      )}
    </form>
  );
}
